import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { formatObservationValue } from "helpers";
type SelectableObservationStockUnit = {
  id: number;
  observation: string | null;
};

type StockUnitSelectionModalProps = {
  open: boolean;
  amount: number;
  stockUnits: SelectableObservationStockUnit[];
  title?: string;
  subtitle?: string | null;
  quantityLabel?: string;
  availabilityLabel?: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: (selectedStockUnitIds: number[]) => void;
};

type ObservationSelectionGroup = {
  key: string;
  observation: string | null;
  stock_unit_ids: number[];
  available_count: number;
  default_selected_count: number;
};

const EMPTY_OBSERVATION_KEY = "__empty_observation__";

const getObservationGroupKey = (observation: string | null) =>
  observation ?? EMPTY_OBSERVATION_KEY;

const buildObservationSelectionGroups = (
  stockUnits: SelectableObservationStockUnit[],
  amount: number,
): ObservationSelectionGroup[] => {
  const groups = new Map<string, ObservationSelectionGroup>();
  const defaultSelectedIds = new Set(
    stockUnits.slice(0, amount).map((stockUnit) => stockUnit.id),
  );

  stockUnits.forEach((stockUnit) => {
    const key = getObservationGroupKey(stockUnit.observation);
    const current = groups.get(key);

    if (current) {
      current.stock_unit_ids.push(stockUnit.id);
      current.available_count += 1;
      if (defaultSelectedIds.has(stockUnit.id)) {
        current.default_selected_count += 1;
      }
      return;
    }

    groups.set(key, {
      key,
      observation: stockUnit.observation ?? null,
      stock_unit_ids: [stockUnit.id],
      available_count: 1,
      default_selected_count: defaultSelectedIds.has(stockUnit.id) ? 1 : 0,
    });
  });

  return Array.from(groups.values());
};

export default function StockUnitSelectionModal({
  open,
  amount,
  stockUnits,
  title = "Seleccionar unidades a egresar",
  subtitle = null,
  quantityLabel = "Cantidad a egresar",
  availabilityLabel = "Disponible para esta observación",
  confirmLabel = "Confirmar selección",
  onClose,
  onConfirm,
}: StockUnitSelectionModalProps) {
  const groups = useMemo(
    () => buildObservationSelectionGroups(stockUnits, amount),
    [amount, stockUnits],
  );
  const [selectedCounts, setSelectedCounts] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    if (!open) {
      setSelectedCounts({});
      return;
    }

    setSelectedCounts(
      groups.reduce<Record<string, string>>((acc, group) => {
        acc[group.key] = String(group.default_selected_count);
        return acc;
      }, {}),
    );
  }, [groups, open]);

  const totalSelected = groups.reduce((sum, group) => {
    const nextValue = Number(selectedCounts[group.key] ?? 0);
    return sum + (Number.isFinite(nextValue) ? nextValue : 0);
  }, 0);

  const hasInvalidGroupCount = groups.some((group) => {
    const nextValue = Number(selectedCounts[group.key] ?? 0);

    return (
      !Number.isInteger(nextValue) ||
      nextValue < 0 ||
      nextValue > group.available_count
    );
  });

  const canConfirm =
    amount > 0 &&
    groups.length > 0 &&
    !hasInvalidGroupCount &&
    totalSelected === amount;

  const handleConfirm = () => {
    const selectedIdsSet = new Set<number>();

    groups.forEach((group) => {
      const groupCount = Number(selectedCounts[group.key] ?? 0);
      group.stock_unit_ids.slice(0, groupCount).forEach((stockUnitId) => {
        selectedIdsSet.add(stockUnitId);
      });
    });

    const orderedSelectedIds = stockUnits
      .filter((stockUnit) => selectedIdsSet.has(stockUnit.id))
      .map((stockUnit) => stockUnit.id);

    onConfirm(orderedSelectedIds);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}

          {groups.map((group) => {
            const currentValue = Number(selectedCounts[group.key] ?? 0);
            const hasError =
              !Number.isInteger(currentValue) ||
              currentValue < 0 ||
              currentValue > group.available_count;

            return (
              <Box
                key={group.key}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {formatObservationValue(group.observation)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {availabilityLabel}: {group.available_count}
                    </Typography>
                  </Box>

                  <TextField
                    label={quantityLabel}
                    type="number"
                    size="small"
                    value={selectedCounts[group.key] ?? "0"}
                    onChange={(event) =>
                      setSelectedCounts((current) => ({
                        ...current,
                        [group.key]: event.target.value,
                      }))
                    }
                    error={hasError}
                    helperText={`Maximo ${group.available_count}`}
                    inputProps={{ min: 0, max: group.available_count }}
                    sx={{ width: { xs: "100%", md: 220 } }}
                  />
                </Stack>
              </Box>
            );
          })}

          <Box
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              pt: 2,
            }}
          >
            <Typography variant="body2">
              Seleccionado: <strong>{totalSelected}</strong> de{" "}
              <strong>{amount}</strong>
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!canConfirm}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
